// Server-authoritative chat session helpers.
//
// Pre-v2 this file owned a localStorage-backed CRUD layer for chats and
// folders. The blob grew to include heavy per-message events
// (planning/profile/graph_traversal/IOCs) and the per-session
// accumulated graph, eventually hitting the ~5 MB origin quota.
//
// After v2 the server (FastAPI /chats/*) is authoritative for chat
// content. This module now exists for two narrow purposes:
//   1. `deriveTitle` — pure helper still used by streaming code and tests
//      to compute a session title from the first user message.
//   2. Re-export of the typed API functions from feedbackApi under
//      `api*` aliases, matching the names used by lib/queries.ts.
//
// Folder localStorage caches (legacy keys: `malpedia-folders`,
// `malpedia-folder-collapse`) have moved server-side; the keys are
// removed during the v2 migration in chatBackfill.ts.

import type { StoredMessage } from '../types';
import {
  deleteChat,
  getChat,
  listChats,
  patchChat,
  updateAccumulatedGraph,
} from './feedbackApi';

export const apiListChats = listChats;
export const apiGetChat = getChat;
export const apiPatchChat = patchChat;
export const apiDeleteChat = deleteChat;
export const apiUpdateAccumulatedGraph = updateAccumulatedGraph;

/** Derive a short title (≤50 chars) from the first user message. Used at
 *  stream-start to lazily fill an empty chat's title without waiting on
 *  the server, and again at stream-end if the server's title is still
 *  empty. Pure — no side effects. */
export function deriveTitle(messages: StoredMessage[]): string {
  const first = messages.find((m) => m.role === 'user');
  if (!first) return '';
  const trimmed = first.content.trim();
  return trimmed.length <= 50 ? trimmed : trimmed.slice(0, 50).trimEnd();
}
